// Sovereign MUSE — visible-reasoning sentinel helpers.
//
// splitReasoning() runs on ASSISTANT content only. It is never called on user
// message content — user messages are never accumulated into reasoningRaw and
// the response_chunk handler that calls this function only fires inside the
// `case 'response_chunk':` branch, which is always an assistant turn.

export const REASONING_OPEN = '<<<REASONING>>>';
export const REASONING_CLOSE = '<<<END_REASONING>>>';

/**
 * Splits raw accumulated response content on the sentinel markers.
 *
 * Returns:
 *   reasoning — text between the opening and closing sentinel (empty string if
 *               neither has appeared yet, or no opening sentinel found at all).
 *   answer    — text after the closing sentinel (empty until it closes).
 *   complete  — true once the closing sentinel has been found.
 *
 * When no opening sentinel is present, reasoning is '' and answer is raw
 * (normal message, no reasoning block created).
 *
 * NOTE: This function only runs on assistant-content buffers. Calling it on
 * user-message content would be a misuse — the contract is one-sided by design
 * (user messages never contain the sentinel, and even if they did the
 * response_chunk handler would never see them).
 */
export function splitReasoning(raw: string): { reasoning: string; answer: string; complete: boolean } {
  const openIdx = raw.indexOf(REASONING_OPEN);
  if (openIdx === -1) {
    // No sentinel at all — normal message.
    return { reasoning: '', answer: raw, complete: false };
  }
  const afterOpen = raw.slice(openIdx + REASONING_OPEN.length);
  const closeIdx = afterOpen.indexOf(REASONING_CLOSE);
  if (closeIdx === -1) {
    // Opening found but not yet closed — reasoning is still streaming.
    return { reasoning: afterOpen.trimStart(), answer: '', complete: false };
  }
  const reasoning = afterOpen.slice(0, closeIdx).trimStart().trimEnd();
  const answer = afterOpen.slice(closeIdx + REASONING_CLOSE.length).trimStart();
  return { reasoning, answer, complete: true };
}
